import type { DecisionResponse } from "@sauron/contracts"
import { useMutation, useQuery } from "@tanstack/react-query"
import { type FormEvent, useState } from "react"
import { Link } from "react-router-dom"
import { api, type Registry } from "@/api.js"
import { Async } from "@/components/Async.js"
import { PageHeader } from "@/components/Page.js"
import { DecisionBadge, DegradedFlag, ReasonBadge } from "@/components/Status.js"
import { Button } from "@/components/ui/button.js"
import { Input, Label } from "@/components/ui/input.js"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select.js"
import { money, toMinorUnits } from "@/lib/money.js"
import { cn } from "@/lib/utils.js"
import { useQuestions } from "@/questions.js"

const BAR: Record<string, string> = {
  ALLOW: "border-allow",
  PREPAYMENT: "border-prepay",
  REVIEW: "border-review",
}

/**
 * Asking here is a **real decision**: recorded like any other, asking the
 * registers live where a decision would, and a paid check attributed to the
 * back office. There is deliberately no unlogged, unpriced way to ask — that
 * would be a hole in constraint 7 exactly where it is most convenient to make
 * one. The one sentence under the form is that caveat, next to the button that
 * triggers it.
 *
 * A decision is the answer to a question, so the form asks which. With one
 * question in this build the control has one option and opens on it; it is
 * still rendered, because a person should see which question they are about
 * to ask rather than discover it from the answer.
 *
 * **The form waits for the registry, and that is a fix rather than a
 * nicety.** It used to mount at once, with the question select controlled to
 * `""` and holding no items, and take the first question — value and item in
 * the same render — when the registry answered. A Radix `Select` mirrors its
 * value into a hidden native `<select>` for forms, and on a value change it
 * assigns the new value there and dispatches a `change` event; the option for
 * it is registered by the item's own effect, one render later, so at that
 * moment the native select fell back to `""` and the change bubbled up as
 * `onValueChange("")`. The form kept that as the chosen question and sent
 * `question: ""`, which the API refused, so nobody could ask from the panel at
 * all (found 2026-09-15). The override form never had the bug because its
 * registry is loaded before it mounts, and this now does the same.
 */
export function CheckTaxIdScreen() {
  const registry = useQuery({ queryKey: ["registry"], queryFn: api.registry })

  return (
    <div className="space-y-7">
      <PageHeader title="Sprawdź NIP" />
      <Async state={registry}>{(loaded) => <CheckForm registry={loaded} />}</Async>
    </div>
  )
}

type CartValue = { readonly amount: number; readonly currency: string }

interface CheckInput {
  readonly taxId: string
  readonly question: string
  readonly cartValue?: CartValue
}

/** What the two cart boxes hold: nothing, a value to send, or why not. */
type CartReading =
  | { readonly kind: "none" }
  | { readonly kind: "value"; readonly value: CartValue }
  | { readonly kind: "problem"; readonly field: "amount" | "currency"; readonly problem: string }

/**
 * **The cart value is typed in złoty, while a condition's threshold is typed
 * in grosze, and the difference is deliberate.** The condition builder holds a
 * stored number it must not round; this box holds what a person reads off an
 * order screen — `1 234,56` — and turns it into minor units exactly, as a
 * string rather than through a float. What it will send is printed beneath it,
 * so somebody who has just read `30000 gr` in a condition and types `30000`
 * here sees thirty thousand złoty before asking.
 *
 * Empty is a value too: a check without a cart is a check of an order without
 * one, and a rule conditioned on the cart does not run.
 */
function readCart(cart: string, currency: string): CartReading {
  if (cart.trim() === "") return { kind: "none" }
  const amount = toMinorUnits(cart)
  if (amount === null) {
    return {
      kind: "problem",
      field: "amount",
      problem: "Kwota: liczba z najwyżej dwoma miejscami po przecinku, np. 350,50.",
    }
  }
  if (!/^[A-Z]{3}$/.test(currency)) {
    return { kind: "problem", field: "currency", problem: "Waluta: trzyliterowy kod, np. PLN." }
  }
  return { kind: "value", value: { amount, currency } }
}

function CheckForm({ registry }: { registry: Registry }) {
  const [taxId, setTaxId] = useState("")
  // The list the select is built from comes with the loaded registry, so the
  // select mounts with its value and its items together — see above.
  const { questions } = registry
  const [question, setQuestion] = useState<string | null>(null)
  const asked = question ?? questions[0]?.questionId ?? null
  const [cart, setCart] = useState("")
  const [currency, setCurrency] = useState("PLN")
  const reading = readCart(cart, currency)

  const check = useMutation({
    mutationFn: (input: CheckInput) =>
      api.checkTaxId(input.taxId, input.question, crypto.randomUUID(), input.cartValue),
  })

  function submit(event: FormEvent) {
    event.preventDefault()
    if (taxId.trim().length > 0 && asked !== null && reading.kind !== "problem") {
      check.mutate({
        taxId: taxId.trim(),
        question: asked,
        ...(reading.kind === "value" ? { cartValue: reading.value } : {}),
      })
    }
  }

  return (
    <div className="space-y-7">
      <div className="max-w-xl space-y-2">
        <form onSubmit={submit} className="space-y-3">
          <div className="flex flex-wrap items-end gap-2.5">
            <div className="min-w-56 flex-1">
              <Label className="mb-1.5 block" htmlFor="check-nip">
                NIP
              </Label>
              <Input
                id="check-nip"
                className="font-mono text-[13px]"
                value={taxId}
                onChange={(event) => setTaxId(event.target.value)}
                placeholder="PL5252445767"
              />
            </div>
            <div>
              <Label className="mb-1.5 block" htmlFor="check-question">
                Pytanie
              </Label>
              <Select value={asked ?? ""} onValueChange={setQuestion}>
                <SelectTrigger id="check-question" className="w-52" aria-label="Pytanie">
                  <SelectValue placeholder="Pytanie" />
                </SelectTrigger>
                <SelectContent>
                  {questions.map((entry) => (
                    <SelectItem key={entry.questionId} value={entry.questionId}>
                      {entry.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex flex-wrap items-end gap-2.5">
            <CartFields
              cart={cart}
              currency={currency}
              reading={reading}
              onCart={setCart}
              onCurrency={setCurrency}
            />
            <Button
              type="submit"
              disabled={check.isPending || asked === null || reading.kind === "problem"}
            >
              {check.isPending ? "Pytam…" : "Zapytaj"}
            </Button>
          </div>
          {reading.kind !== "none" && (
            <p
              id="check-cart-value"
              className={cn(
                "text-[13px] tabular-nums",
                reading.kind === "value" ? "text-muted-foreground" : "text-critical",
              )}
            >
              {reading.kind === "value" ? `= ${money(reading.value)}` : reading.problem}
            </p>
          )}
        </form>
        <p className="text-[13px] text-muted-foreground">
          Prawdziwa decyzja: zostanie zapisana, a rejestry zapytane jak przy zamówieniu. Wartość
          koszyka może uruchomić płatne sprawdzenie w KRD.
        </p>
      </div>

      {check.error !== null && check.error !== undefined && (
        <p className="text-sm text-critical">
          {check.error instanceof Error ? check.error.message : "Coś poszło nie tak."}
        </p>
      )}

      {check.data !== undefined && (
        <CheckResult answer={check.data} cartValue={check.variables?.cartValue} />
      )}
    </div>
  )
}

function CartFields(props: {
  readonly cart: string
  readonly currency: string
  readonly reading: CartReading
  readonly onCart: (value: string) => void
  readonly onCurrency: (value: string) => void
}) {
  const { reading } = props
  return (
    <>
      <div>
        <Label className="mb-1.5 block" htmlFor="check-cart">
          Wartość koszyka
        </Label>
        <Input
          id="check-cart"
          inputMode="decimal"
          className="w-36 tabular-nums"
          value={props.cart}
          onChange={(event) => props.onCart(event.target.value)}
          placeholder="opcjonalnie"
          aria-invalid={reading.kind === "problem" && reading.field === "amount"}
          aria-describedby={reading.kind === "none" ? undefined : "check-cart-value"}
        />
      </div>
      <div>
        <Label className="mb-1.5 block" htmlFor="check-currency">
          Waluta
        </Label>
        <Input
          id="check-currency"
          className="w-[5.5rem] uppercase"
          maxLength={3}
          value={props.currency}
          onChange={(event) => props.onCurrency(event.target.value.toUpperCase())}
          aria-invalid={reading.kind === "problem" && reading.field === "currency"}
        />
      </div>
    </>
  )
}

/**
 * The answer, and what it was asked with. The reasons say what the rules
 * found, not which sources were asked — a source that found nothing writes no
 * reason — so the decision's own screen, which lists every call, KRD's
 * included, is one click away.
 */
function CheckResult({
  answer,
  cartValue,
}: {
  readonly answer: DecisionResponse
  readonly cartValue: CartValue | undefined
}) {
  const { titleOf } = useQuestions()
  return (
    <div className={cn("border-l-[3px] py-1 pl-5", BAR[answer.decision] ?? "border-prepay")}>
      <p className="text-[13px] text-muted-foreground">
        {titleOf(answer.question)}
        {cartValue === undefined ? " · bez wartości koszyka" : ` · koszyk ${money(cartValue)}`}
      </p>
      <div className="mt-1 flex flex-wrap items-center gap-4">
        <DecisionBadge decision={answer.decision as "ALLOW" | "PREPAYMENT" | "REVIEW"} size="lg" />
        <DegradedFlag degraded={answer.degraded} />
      </div>
      {answer.reasons.length > 0 && (
        <p className="mt-2 flex flex-wrap gap-x-3 gap-y-0.5">
          {answer.reasons.map((reason) => (
            <ReasonBadge key={reason.code} code={reason.code} kind={reason.kind} />
          ))}
        </p>
      )}
      <p className="mt-2 font-mono text-xs text-muted-foreground">
        <Link className="hover:text-foreground" to={`/decisions/${answer.decisionId}`}>
          {answer.decisionId}
        </Link>{" "}
        · konfiguracja v{answer.configVersion}
      </p>
    </div>
  )
}
